import React, { useState } from "react";
import DOMPurify from "dompurify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTheme } from "../../../context/ThemeContext";
import { useTranslation } from "../../../../../i18n/LanguageContext";
import EmptyStatePanel from "../../common/EmptyStatePanel";
import {
  faEye,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faClipboardList,
  faShareAlt,
} from "@fortawesome/free-solid-svg-icons";
import OreInsightCard from "../../common/OreInsightCard";
import SuggestionsBulletList from "../../common/SuggestionsBulletList";
import ThreatBannerShareButton from "../../common/ThreatBannerShareButton";
import InlineShareForm from "../../common/InlineShareForm";
import useShareAlert from "../../common/useShareAlert";
import { useSubscription } from "../../../context/SubscriptionContext";

const SUMMARY_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "h3",
  "h4",
  "h5",
];

/**
 * Sanitize HTML content, handling double-encoded entities from legacy data.
 * If DOMPurify output still contains &lt; / &gt; entities, decode and re-sanitize.
 */
const sanitizeHtml = (html, allowedTags) => {
  if (!html) return "";
  const config = { ALLOWED_TAGS: allowedTags, ALLOWED_ATTR: [] };
  let cleaned = DOMPurify.sanitize(html, config);

  // Detect double-encoded HTML (e.g. &lt;p&gt; stored in DB from escapeHtml)
  if (cleaned.includes("&lt;") && cleaned.includes("&gt;")) {
    const temp = document.createElement("div");
    temp.innerHTML = cleaned;
    cleaned = DOMPurify.sanitize(
      temp.textContent || temp.innerText || "",
      config,
    );
  }

  return cleaned;
};

const AnalysisDetails = ({
  analysisResult,
  selectedHistoryItem,
  onPreviousCase,
  onNextCase,
  canNavigatePrevious,
  canNavigateNext,
  isMobile = false,
  isOptimized = false,
  reportId,
}) => {
  // Theme context
  const { effectiveTheme } = useTheme();
  const isDarkTheme = effectiveTheme === "dark";
  const { t } = useTranslation();

  // Inline share state (mobile only)
  const share = useShareAlert();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();

  // Helper: normalize AI recommendation text so the risk prefix matches actual analysis data
  const getNormalizedRecommendation = (item) => {
    if (!item?.suggestions) return null;
    let text = item.suggestions;

    // Determine the correct risk label from analysisStatus and isScam.
    // NOTE: confidence measures how sure the AI is of its own answer,
    // NOT the severity of the scam — so we don't use it for risk labeling.
    // analysisStatus already encodes both the determination and confidence.
    let correctPrefix = null;
    if (item.analysisStatus) {
      const s = item.analysisStatus.toLowerCase();
      if (s.includes("very likely") && s.includes("scam")) {
        correctPrefix = "High-risk";
      } else if (s.includes("potentially") && s.includes("scam")) {
        correctPrefix = "Medium-risk";
      } else if (s.includes("unlikely") && s.includes("scam")) {
        correctPrefix = "Low-risk";
      } else if (s.includes("legitimate")) {
        correctPrefix = "Low-risk";
      }
    }
    // Fallback: if analysisStatus is missing, use isScam boolean directly.
    // If the AI concluded it IS a scam, treat it as high-risk regardless of
    // confidence (that only reflects the AI's certainty, not severity).
    if (!correctPrefix && typeof item.isScam === "boolean") {
      correctPrefix = item.isScam ? "High-risk" : "Low-risk";
    }

    // Strip any existing risk-level prefix the AI may have inserted
    const prefixPattern =
      /^(high-risk|medium-risk|low-risk|no-risk|critical-risk|very high-risk)\s*[:–—-]\s*/i;
    if (correctPrefix && prefixPattern.test(text)) {
      text = text.replace(prefixPattern, correctPrefix + ": ");
    } else if (correctPrefix && !prefixPattern.test(text)) {
      // No prefix present — prepend the correct one
      text = correctPrefix + ": " + text;
    }

    return text;
  };

  // Helper functions
  const isSystemError = (item) => {
    if (!item || !item.explanation) return false;
    return (
      item.explanation.includes("SYSTEM_ERROR") ||
      item.explanation.includes(
        "Analysis was unavailable due to processing error",
      ) ||
      item.explanation.includes("Unable to analyze") ||
      (item.confidence === 0 && item.explanation.includes("technical error"))
    );
  };

  const hasInsufficientData = (item) => {
    if (!item) return false;
    return (
      item.dataSufficiency === "INSUFFICIENT" ||
      item.explanation?.includes("Insufficient Data") ||
      item.explanation?.includes("insufficient data") ||
      item.explanation?.includes("Not enough reliable information") ||
      (item.confidence && item.confidence < 0.4)
    );
  };

  const hasInsufficientUserInput = (userInputs = null) => {
    if (!userInputs) return false;

    const hasBusinessName = userInputs.businessName
      ? userInputs.businessName.trim()
      : "";
    const hasWebsiteUrl = userInputs.websiteUrl
      ? userInputs.websiteUrl.trim()
      : "";

    const hasBasicInfo = hasBusinessName || hasWebsiteUrl;

    if (!hasBasicInfo) {
      return true;
    }

    const optionalFieldsCount = [
      userInputs.merchandiseType,
      userInputs.businessContactName
        ? userInputs.businessContactName.trim()
        : "",
      userInputs.businessInteractionReason,
      userInputs.socialMediaPlatform,
    ].filter(Boolean).length;

    return optionalFieldsCount < 2;
  };

  const getStatusText = (item) => {
    if (isSystemError(item)) {
      return t("social.detail.analysisUnavailable");
    }

    // Return analysisStatus if available (preferred)
    if (item.analysisStatus) {
      return item.analysisStatus;
    }

    // Return conclusion if available (backup)
    if (item.conclusion) {
      return item.conclusion;
    }

    // Handle insufficient data cases
    if (hasInsufficientData(item)) {
      return t("social.detail.insufficientData");
    }

    // Fallback to isScam boolean
    if (typeof item.isScam === "boolean") {
      return item.isScam
        ? t("social.detail.potentialScam")
        : t("social.detail.appearsLegitimate");
    }

    // Ultimate fallback
    return t("social.detail.statusUnknown");
  };

  const getStatusClass = (item) => {
    if (isSystemError(item)) {
      return "security-failed";
    }

    // Handle analysisStatus field
    if (item.analysisStatus) {
      const status = item.analysisStatus.toLowerCase();

      if (status.includes("very likely") && status.includes("scam")) {
        return "threat-detected";
      }
      if (status.includes("potentially") && status.includes("scam")) {
        if (status.includes("insufficient")) {
          return "security-failed";
        }
        return "threat-detected";
      }
      if (status.includes("unlikely") && status.includes("scam")) {
        if (status.includes("insufficient")) {
          return "security-failed";
        }
        return "safe";
      }
      if (status.includes("legitimate")) {
        return "safe";
      }
    }

    // Handle conclusion field as backup
    if (item.conclusion) {
      const conclusion = item.conclusion.toLowerCase();

      if (conclusion.includes("very likely") && conclusion.includes("scam")) {
        return "threat-detected";
      }
      if (conclusion.includes("likely") && conclusion.includes("scam")) {
        return "threat-detected";
      }
      if (conclusion.includes("potentially") && conclusion.includes("scam")) {
        return "threat-detected";
      }
      if (conclusion.includes("unlikely") && conclusion.includes("scam")) {
        return "safe";
      }
      if (
        conclusion.includes("legitimate") ||
        conclusion.includes("safe") ||
        conclusion.includes("trusted")
      ) {
        return "safe";
      }
    }

    // Handle insufficient data cases
    if (hasInsufficientData(item)) {
      return "security-failed";
    }

    // Fallback to isScam boolean
    if (typeof item.isScam === "boolean") {
      return item.isScam ? "threat-detected" : "safe";
    }

    // Ultimate fallback
    return "security-failed";
  };

  const getStatusIcon = (item) => {
    if (isSystemError(item)) {
      return faExclamationTriangle;
    }

    // Handle analysisStatus field
    if (item.analysisStatus) {
      const status = item.analysisStatus.toLowerCase();

      if (status.includes("very likely") && status.includes("scam")) {
        return faTimesCircle;
      }
      if (status.includes("potentially") && status.includes("scam")) {
        return faTimesCircle;
      }
      if (status.includes("unlikely") && status.includes("scam")) {
        if (status.includes("insufficient")) {
          return faExclamationTriangle;
        }
        return faCheckCircle;
      }
      if (status.includes("legitimate")) {
        return faCheckCircle;
      }
    }

    // Handle conclusion field as backup
    if (item.conclusion) {
      const conclusion = item.conclusion.toLowerCase();

      if (conclusion.includes("very likely") && conclusion.includes("scam")) {
        return faTimesCircle;
      }
      if (conclusion.includes("likely") && conclusion.includes("scam")) {
        return faTimesCircle;
      }
      if (conclusion.includes("potentially") && conclusion.includes("scam")) {
        return faTimesCircle;
      }
      if (conclusion.includes("unlikely") && conclusion.includes("scam")) {
        return faCheckCircle;
      }
      if (
        conclusion.includes("legitimate") ||
        conclusion.includes("safe") ||
        conclusion.includes("trusted")
      ) {
        return faCheckCircle;
      }
    }

    // Handle insufficient data cases
    if (hasInsufficientData(item)) {
      return faExclamationTriangle;
    }

    // Fallback to isScam boolean
    if (typeof item.isScam === "boolean") {
      return item.isScam ? faTimesCircle : faCheckCircle;
    }

    // Ultimate fallback
    return faExclamationTriangle;
  };

  return (
    <div className="right-panel">
      {!isMobile && (
        <div className="common-section-header">
          <h3>
            <FontAwesomeIcon icon={faEye} />
            {t("social.detail.heading")}
          </h3>
        </div>
      )}
      <div className="panel-content details-panel-content">
        {analysisResult || selectedHistoryItem ? (
          <div className="case-details">
            {(() => {
              const item = selectedHistoryItem || analysisResult;
              return (
                <>
                  {/* Threat Assessment Banner */}
                  <div
                    className={`threat-banner ${isMobile ? "sasm-threat-banner" : ""} ${getStatusClass(item)}`}
                  >
                    <div className="threat-icon">
                      <FontAwesomeIcon icon={getStatusIcon(item)} />
                    </div>
                    {isMobile ? (
                      <>
                        <h2 className="threat-title">{getStatusText(item)}</h2>
                        {getStatusClass(item) === "threat-detected" && (
                          <button
                            className="threat-banner-share-btn"
                            onClick={
                              isIndividualPlan
                                ? undefined
                                : (e) => {
                                    e.stopPropagation();
                                    share.toggleShare();
                                  }
                            }
                            disabled={isIndividualPlan}
                            title={
                              isIndividualPlan
                                ? t("social.detail.shareIndividual")
                                : t("social.detail.shareFamily")
                            }
                          >
                            <FontAwesomeIcon
                              icon={faShareAlt}
                              className="threat-share-icon"
                            />
                            <span className="threat-share-label">
                              {t("social.detail.share")}
                            </span>
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="threat-content">
                          <h2 className="threat-title">
                            {item.businessName ||
                              item.websiteUrl ||
                              t("social.detail.unknownBusiness")}
                          </h2>
                          <p className="threat-subtitle">
                            {getStatusText(item)}
                          </p>
                        </div>
                        <ThreatBannerShareButton
                          scamDetected={
                            getStatusClass(item) === "threat-detected"
                          }
                          threatType={t("social.share.threatType")}
                          threatTitle={`${t("social.detail.potentialScam")} — ${item.businessName || item.websiteUrl || ""}`}
                          threatDetail={getStatusText(item)}
                          reportId={reportId}
                        />
                      </>
                    )}
                  </div>

                  {/* Inline Share Form (mobile only) */}
                  {isMobile &&
                    share.shareOpen &&
                    getStatusClass(item) === "threat-detected" && (
                      <InlineShareForm
                        shareSuccess={share.shareSuccess}
                        shareError={share.shareError}
                        shareMessage={share.shareMessage}
                        setShareMessage={share.setShareMessage}
                        shareLoading={share.shareLoading}
                        onCancel={share.closeShare}
                        onSend={() => share.sendShare(reportId)}
                      />
                    )}

                  {/* Insufficient Data Encouragement Message */}
                  {hasInsufficientUserInput(item.userInputs) && (
                    <div className="insufficient-data-notice">
                      <div className="notice-header">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span>{t("social.detail.limitedData")}</span>
                      </div>
                      <div className="notice-content">
                        <p>
                          <strong>{t("social.detail.moreAccurate")}</strong>
                        </p>
                        <ul>
                          {!item.userInputs?.websiteUrl?.trim() && (
                            <li>{t("social.detail.missingWebsite")}</li>
                          )}
                          {!item.userInputs?.merchandiseType && (
                            <li>{t("social.detail.missingMerchandise")}</li>
                          )}
                          {!item.userInputs?.businessContactName?.trim() && (
                            <li>{t("social.detail.missingContact")}</li>
                          )}
                          {!item.userInputs?.businessInteractionReason && (
                            <li>{t("social.detail.missingInteraction")}</li>
                          )}
                          {!item.userInputs?.socialMediaPlatform && (
                            <li>{t("social.detail.missingPlatform")}</li>
                          )}
                          {!item.userInputs?.businessName?.trim() &&
                            item.userInputs?.websiteUrl?.trim() && (
                              <li>{t("social.detail.missingName")}</li>
                            )}
                        </ul>
                        {!item.userInputs?.websiteUrl?.trim() ||
                        !item.userInputs?.merchandiseType ||
                        !item.userInputs?.businessContactName?.trim() ||
                        !item.userInputs?.businessInteractionReason ||
                        !item.userInputs?.socialMediaPlatform ||
                        (!item.userInputs?.businessName?.trim() &&
                          item.userInputs?.websiteUrl?.trim()) ? (
                          <p className="notice-tip">
                            💡 <em>{t("social.detail.fillMissing")}</em>
                          </p>
                        ) : (
                          <p className="notice-tip">
                            💡 <em>{t("social.detail.comprehensiveInfo")}</em>
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {item.suggestions && (
                    <div className="suggestions-section">
                      <h4 className="subsection-title">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          style={{ marginRight: "0.5rem" }}
                        />
                        {t("social.detail.whatToDo")}
                      </h4>
                      <SuggestionsBulletList
                        text={getNormalizedRecommendation(item)}
                      />
                    </div>
                  )}

                  <OreInsightCard
                    oreData={item}
                    channel="social"
                    isMobile={isMobile}
                  />

                  {item.explanation && (
                    <div className="explanation-section">
                      <h4 className="subsection-title">
                        <FontAwesomeIcon
                          icon={faClipboardList}
                          style={{ marginRight: "0.5rem" }}
                        />
                        {t("social.detail.explanation")}
                      </h4>
                      <div
                        className="explanation-text"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(
                            item.explanation,
                            SUMMARY_ALLOWED_TAGS,
                          ),
                        }}
                      />
                    </div>
                  )}

                  {item.missingDataPoints &&
                    item.missingDataPoints.length > 0 && (
                      <div className="common-form-group">
                        <h4>{t("social.detail.additionalData")}</h4>
                        <div className="missing-data-content">
                          <ul>
                            {item.missingDataPoints.map((dataPoint, index) => (
                              <li key={index}>{dataPoint}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                </>
              );
            })()}
          </div>
        ) : (
          <EmptyStatePanel
            icon={faEye}
            title={t("social.detail.noCase")}
            description={t("social.detail.noCaseDesc")}
            className="no-case-selected"
          />
        )}
      </div>
    </div>
  );
};

export default AnalysisDetails;
